import { Component, Input, OnInit, OnDestroy, OnChanges, SimpleChanges, Output, EventEmitter, ChangeDetectorRef } from '@angular/core';
import { NgRedux } from '@angular-redux/store';
import { Subscription, interval } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { CalculationStateService, CalculationOperation, PendingPbSelection, PendingPb } from '../../../optimistic-planning/calculation-state.service';
import { OptimisticPlanningService } from '../../../optimistic-planning/optimistic-planning.service';
import { ConfirmDialogService } from '../../components/confirm-dialog/confirm-dialog.service';
import { UserSettingsService } from '../../../pb-screen/services/usersettings.service';
import { ApplicationUserActions } from '../../../pb-screen/application.user.actions';
import { UserSettings } from '../../../pb-screen/models/application.user.interface';
import { UserFeedbackService } from '../../../services/user-feedback.service';
import { IAppState } from '../../../store/IAppState';

interface OperationGroup {
  userId: string;
  userName: string;
  isOwnUser: boolean;
  calculating: CalculationOperation[];
  completed: CalculationOperation[];
  failed: CalculationOperation[];
}

@Component({
  selector: 'calc-activity-panel',
  templateUrl: './calc-activity-panel.component.html',
  styleUrls: ['./calc-activity-panel.component.scss']
})
export class CalcActivityPanelComponent implements OnInit, OnDestroy, OnChanges {

  @Input() userId: string = '';
  @Input() planDate: string = '';         // Day view: single date (DD/MM/YYYY)
  @Input() planDateFrom: string = '';     // Week view: range start
  @Input() planDateUntil: string = '';    // Week view: range end
  @Output() clearSelection = new EventEmitter<void>();

  pendingSelection: PendingPbSelection | null = null;
  allOperations: CalculationOperation[] = [];
  currentDateOps: CalculationOperation[] = [];
  otherDateOps: CalculationOperation[] = [];

  operationGroups: OperationGroup[] = [];
  now = Date.now();

  reservationSeconds = 0;
  private _reservationSub: Subscription | null = null;

  collapsedOperations: Record<string, boolean> = {};

  private readonly COMPLETED_TTL_MS = 10 * 60 * 1000;
  private readonly FAILED_TTL_MS = 30 * 60 * 1000;
  private sub: Subscription;
  private pendingSub: Subscription;
  private tickSub: Subscription;

  constructor(
    private calcState: CalculationStateService,
    private cdr: ChangeDetectorRef,
    public optimisticService: OptimisticPlanningService,
    private t: TranslateService,
    private confirmDialogService: ConfirmDialogService,
    private ngRedux: NgRedux<IAppState>,
    private userSettingsService: UserSettingsService,
    private userActions: ApplicationUserActions,
    private userFeedback: UserFeedbackService
  ) {}

  tr(key: string, params?: object): string {
    return this.t.instant('OPTIMISTIC.' + key, params);
  }

  /** Slider save-in-flight — the button is disabled meanwhile. */
  saving = false;
  private boardUserSettingRecord: UserSettings | null = null;

  private static readonly BOARD_USER_SETTING = 'enableOptimisticUiPlanningBoardUser';

  /**
   * The slider is this user's PERSONAL preference — it must never touch the tenant-wide
   * "enableOptimisticUiPlanningBoard" switch that Settings → Optimistic UI edits, otherwise one
   * planner flips the feature for the whole company. It therefore writes a user-scoped
   * "enableOptimisticUiPlanningBoardUser" record. The backend value is the single source of
   * truth (re-applied on every login/user emission), so the flip must be persisted — a
   * client-side-only flip gets overwritten on refresh.
   */
  async toggle(): Promise<void> {
    if (this.saving) return;
    // Nothing to toggle while the company has the feature switched off.
    if (!this.optimisticService.isBoardGeneralEnabled) return;

    const previous = this.optimisticService.isBoardUserEnabled;
    const next = !previous;
    this.saving = true;
    // Instant visual feedback; reverted below if the save fails.
    this.optimisticService.setBoardUserEnabled(next);

    try {
      const state = this.ngRedux.getState();
      const userId = state.applicationUser?.id;
      // A record without a userId is the tenant-level setting — updating it would change the
      // value for everyone, so only a genuinely user-scoped record may be reused.
      const record = this.boardUserSettingRecord
        || (state.applicationUser?.settings || []).find(
          (s: UserSettings) => s.setting?.name === CalcActivityPanelComponent.BOARD_USER_SETTING && !!s.userId);

      if (record) {
        const updated = { ...record, value: String(next) };
        const res: any = await this.userSettingsService.updateUserSettings(updated).toPromise();
        this.boardUserSettingRecord = { ...((res && res.data) || updated), userId };
      } else {
        const settingDef = await this.userSettingsService
          .getSettingByName(CalcActivityPanelComponent.BOARD_USER_SETTING, true).toPromise();
        const created = await this.userSettingsService
          .createUserSettings(userId, settingDef, String(next), null, null).toPromise();
        this.boardUserSettingRecord = { ...created, userId };
      }

      // Keep the redux user in sync so the app.component setter loop agrees with the slider, and
      // carry the setting record itself so a freshly mounted panel (day ↔ week board) finds it
      // instead of creating a second user-scoped record for the same setting.
      const user = state.applicationUser;
      if (user) {
        const settings = (user.settings || []).filter((s: UserSettings) =>
          !(s.setting?.name === CalcActivityPanelComponent.BOARD_USER_SETTING && !!s.userId));
        settings.push(this.boardUserSettingRecord);
        this.userActions.updateApplicationUserSucceded(
          { ...user, settings, enableOptimisticUiPlanningBoardUser: next }, false);
      }
    } catch (e) {
      this.optimisticService.setBoardUserEnabled(previous);
      this.userFeedback.showErrorMessageWithoutTranslating('Could not save the Optimistic UI setting — change reverted.');
    } finally {
      this.saving = false;
      this.cdr.markForCheck();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['planDate'] || changes['planDateFrom'] || changes['planDateUntil']) {
      this._splitByDate();
    }
  }

  ngOnInit() {
    this.sub = this.calcState.operations$.subscribe(ops => {
      this.allOperations = ops;
      this._splitByDate();
      this._rebuildOperationGroups();
    });

    this.pendingSub = this.calcState.pendingPbs$.subscribe(sel => {
      const hadPbs = !!this.pendingSelection?.pbs?.length;
      const hasPbs = !!sel?.pbs?.length;
      this.pendingSelection = sel;
      this.cdr.markForCheck();

      if (hasPbs && !hadPbs) {
        this._startCountdown();
      } else if (!hasPbs) {
        this._stopCountdown();
      }
    });

    this.tickSub = interval(1000).subscribe(() => {
      this.now = Date.now();
      this.calcState.expireStuckOperations();
      for (const op of this.allOperations) {
        if (op.expiresAt && new Date(op.expiresAt).getTime() <= this.now) {
          this.calcState.dismissOperation(op.operationId).subscribe();
        }
      }
    });
  }

  ngOnDestroy() {
    this.sub?.unsubscribe();
    this.pendingSub?.unsubscribe();
    this.tickSub?.unsubscribe();
    this._reservationSub?.unsubscribe();
  }

  // ── Reservation countdown (dead simple) ──

  private _startCountdown(): void {
    this._stopCountdown();
    this.reservationSeconds = 30;
    this._reservationSub = interval(1000).subscribe(() => {
      if (this.reservationSeconds > 0) {
        this.reservationSeconds--;
      }
    });
  }

  private _stopCountdown(): void {
    this.reservationSeconds = 0;
    this._reservationSub?.unsubscribe();
    this._reservationSub = null;
  }

  get reservationCountdownText(): string {
    if (this.reservationSeconds > 0) return this.tr('RESERVED_FOR', { seconds: this.reservationSeconds });
    if (this.pendingSelection?.pbs?.length) return this.tr('RESERVATION_EXPIRED');
    return '';
  }

  // ── Collapse ──

  toggleOperationCollapse(userId: string): void {
    this.collapsedOperations = { ...this.collapsedOperations, [userId]: !this.collapsedOperations[userId] };
    this.cdr.detectChanges();
  }

  // ── Rebuild methods (called on data change, not on every tick) ──

  private _displayName(userName?: string, userId?: string): string {
    const raw = userName || userId || '';
    // Strip email suffix: "Stefaan Van Hoof(vanhost@crono...)" → "Stefaan Van Hoof"
    return raw.replace(/\s*\(.*\)\s*$/, '').trim() || raw;
  }

  private _rebuildOperationGroups(): void {
    // Only show current user's operations (multi-user visibility descoped)
    const ownOps = this.allOperations.filter(op => op.userId === this.userId);
    if (ownOps.length === 0) {
      this.operationGroups = [];
      return;
    }

    const group: OperationGroup = {
      userId: this.userId,
      userName: this._displayName(ownOps[0]?.userName, this.userId),
      isOwnUser: true,
      calculating: [],
      completed: [],
      failed: []
    };
    for (const op of ownOps) {
      if (op.status === 'Calculating') group.calculating.push(op);
      else if (op.status === 'Completed') group.completed.push(op);
      else if (op.status === 'Failed') group.failed.push(op);
    }
    this.operationGroups = [group];
  }

  /** Split operations into current-date vs other-date based on planDate input(s) */
  private _splitByDate(): void {
    const ownOps = this.allOperations.filter(op => op.userId === this.userId);
    this.currentDateOps = [];
    this.otherDateOps = [];
    for (const op of ownOps) {
      if (this._isCurrentDate(op)) {
        this.currentDateOps.push(op);
      } else {
        this.otherDateOps.push(op);
      }
    }
  }

  /** Check if an operation's display date falls within the current view date (range).
   *  Prefers uiPlanDate (UI-only, reflects the action's actual target day)
   *  and falls back to planDate (which is also the value sent to the backend). */
  private _isCurrentDate(op: CalculationOperation): boolean {
    const displayDate = op.uiPlanDate || op.planDate;
    if (!displayDate) return true; // No date = assume current
    const opDate = this._normalizeDate(displayDate);
    if (!opDate) return true;

    // Week view: check range
    if (this.planDateFrom && this.planDateUntil) {
      const from = this._normalizeDate(this.planDateFrom);
      const until = this._normalizeDate(this.planDateUntil);
      if (from && until) return opDate >= from && opDate <= until;
    }
    // Day view: exact match
    if (this.planDate) {
      const current = this._normalizeDate(this.planDate);
      if (current) return opDate === current;
    }
    return true; // No context = assume current
  }

  /** Normalize various date formats to YYYY-MM-DD for comparison */
  private _normalizeDate(dateStr: string): string | null {
    if (!dateStr) return null;
    // Try ISO format (2026-02-09T...)
    if (dateStr.includes('T') || dateStr.match(/^\d{4}-\d{2}-\d{2}/)) {
      return dateStr.substring(0, 10);
    }
    // Try DD/MM/YYYY
    const parts = dateStr.split('/');
    if (parts.length === 3 && parts[0].length <= 2) {
      return `${parts[2]}-${parts[1].padStart(2, '0')}-${parts[0].padStart(2, '0')}`;
    }
    return null;
  }

  /** Format the op's display date (short: DD/MM). Prefers uiPlanDate over planDate. */
  formatPlanDate(op: CalculationOperation): string {
    const displayDate = op.uiPlanDate || op.planDate;
    if (!displayDate) return '';
    const norm = this._normalizeDate(displayDate);
    if (!norm) return '';
    const parts = norm.split('-');
    return `${parts[2]}/${parts[1]}`;
  }

  // ── Derived state ──

  get hasPendingPbs(): boolean {
    return !!this.pendingSelection?.pbs?.length;
  }

  get pendingPbs(): PendingPb[] { return this.pendingSelection?.pbs || []; }

  get pendingPbsLocked(): boolean {
    if (!this.pendingSelection?.pbs?.length) return false;
    const ids = new Set(this.pendingSelection.pbs.map(p => p.id));
    return this.allOperations.some(op => op.status === 'Calculating' && op.planningBlockIds?.some(id => ids.has(id)));
  }

  opGroupTotal(g: OperationGroup): number {
    return g.calculating.length + g.completed.length + g.failed.length;
  }

  get hasAnyOperations(): boolean { return this.allOperations.length > 0; }

  get isEmpty(): boolean { return !this.hasPendingPbs && !this.hasAnyOperations; }

  get activeCount(): number { return this.allOperations.filter(op => op.status === 'Calculating').length; }

  // ── Dot helpers ──

  isDotCompleted(op: CalculationOperation): boolean {
    return op.status === 'Completed' && !this.isDotWarning(op) && !this.hasResultError(op);
  }

  isDotWarning(op: CalculationOperation): boolean {
    return op.status === 'Completed' &&
      op.result?.success !== false &&
      !!op.result?.conflicts?.some(c => c.severity === 'warning' || c.severity === 'error');
  }

  hasResultError(op: CalculationOperation): boolean {
    return op.status === 'Completed' && op.result?.success === false;
  }

  hasQueuedRing(op: CalculationOperation): boolean { return !!op.queuedAction; }

  queuedRingClass(op: CalculationOperation): string {
    if (op.queuedActionStatus === 'failed' || op.queuedActionStatus === 'skipped') return 'cap__ring--fail';
    if (op.queuedActionStatus === 'completed') return 'cap__ring--ok';
    if (op.queuedActionStatus === 'executing') return 'cap__ring--exec';
    return '';
  }

  // ── Labels ──

  getTypeLabel(op: CalculationOperation): string {
    const map: Record<string, string> = {
      plan: 'TYPE_PLAN', move: 'TYPE_MOVE', assign: 'TYPE_ASSIGN',
      sendPb: 'TYPE_SEND_PB', cancelPb: 'TYPE_CANCEL_PB', unplanPb: 'TYPE_UNPLAN_PB',
      cancelAndUnplanPb: 'TYPE_CANCEL_AND_UNPLAN_PB', replanPb: 'TYPE_REPLAN_PB',
      cancelAndReplanPb: 'TYPE_CANCEL_AND_REPLAN_PB',
      sendTo: 'TYPE_SEND_TO', cancelTo: 'TYPE_CANCEL_TO', unplanTo: 'TYPE_UNPLAN_TO',
      cancelAndUnplanTo: 'TYPE_CANCEL_AND_UNPLAN_TO', resendTo: 'TYPE_RESEND_TO',
      sendNextPb: 'TYPE_SEND_NEXT_PB', removeCapacity: 'TYPE_REMOVE_CAPACITY'
    };
    return this.tr(map[op.operationType] || 'TYPE_DEFAULT');
  }

  getSubtitle(op: CalculationOperation): string {
    const parts: string[] = [];
    if (op.driverName) parts.push(op.driverName);
    if (op.truckName) parts.push(op.truckName);
    return parts.join(' · ');
  }

  getElapsedLabel(op: CalculationOperation): string {
    return Math.floor((this.now - new Date(op.startedAt).getTime()) / 1000) + 's';
  }

  getResultHeadline(op: CalculationOperation): string {
    if (op.status === 'Failed') return op.result?.errorMessage || this.tr('DEFAULT_ERROR');
    return op.result?.summary?.headline || this.tr('DEFAULT_PLANNED');
  }

  getAutoClearLabel(op: CalculationOperation): string {
    if (!op.expiresAt) return '';
    const remaining = Math.max(0, Math.floor((new Date(op.expiresAt).getTime() - this.now) / 1000));
    if (remaining <= 0) return this.tr('CLEARING');
    const prefix = this.tr('AUTO_CLEARS_IN');
    if (remaining < 60) return `${prefix} ${remaining}s`;
    const m = Math.floor(remaining / 60);
    const s = remaining % 60;
    return s > 0 ? `${prefix} ${m}m ${s}s` : `${prefix} ${m}m`;
  }

  getExpiryPercent(op: CalculationOperation): number {
    if (!op.expiresAt || !op.completedAt) return 100;
    const totalTtl = op.status === 'Failed' ? this.FAILED_TTL_MS : this.COMPLETED_TTL_MS;
    const remaining = new Date(op.expiresAt).getTime() - this.now;
    return Math.max(0, Math.min(100, (remaining / totalTtl) * 100));
  }

  // ── Actions ──

  onRowClick(op: CalculationOperation): void {
    if (!op.truckName && !op.driverName) return;
    this.calcState.pinSlot(op.truckName || '', op.driverName || '', op.operationId);
  }

  dismiss(op: CalculationOperation, event: Event): void {
    event.stopPropagation();
    this.calcState.dismissOperation(op.operationId).subscribe();
  }

  get hasFinishedOps(): boolean {
    return this.allOperations.some(op => op.status === 'Completed' || op.status === 'Failed');
  }

  clearAllOperations(): void {
    this.confirmDialogService.confirmThis(
      this.tr('CLEAR_ALL_CONFIRM'),
      () => {
        this.calcState.dismissAllFinished();
      },
      () => {}
    );
  }
}
